/**
 * Buscador del menú lateral
 * -----------------------------------------------------------------------------
 * Filtra los enlaces del menú a medida que se escribe. Se activa poniendo
 * `data-company-sidenav-search` en el elemento del sidenav:
 *
 *   <nav class="sidenav owari-sidenav" data-mdb-sidenav-init data-company-sidenav-search>
 *
 * Si el markup ya trae un `.sidenav-search input`, se usa ese; si no, se crea y
 * se coloca en `.sidenav-header`.
 *
 *
 * Grupos desplegables
 * -------------------
 * Un submenú es un `<ul class="sidenav-collapse">` anidado detrás del enlace
 * que lo abre. MDB le pone solo el plegado, el `id` y la flecha:
 *
 *   <li class="sidenav-item">
 *     <a class="sidenav-link"><i class="fas fa-building"></i><span>Empresas</span></a>
 *     <ul class="sidenav-collapse">
 *       <li class="sidenav-item">
 *         <a class="sidenav-link" href="/empresa"><i class="fas fa-id-card"></i><span>Empresa</span></a>
 *       </li>
 *     </ul>
 *   </li>
 *
 * Para que un grupo arranque abierto: `<ul class="sidenav-collapse show">`.
 *
 * El buscador los tiene en cuenta: un resultado dentro de un grupo cerrado
 * abre el grupo, y al vaciar el campo el menú vuelve a como estaba.
 *
 * Opciones:
 *   data-company-sidenav-search-min="15"  solo aparece si hay más de N enlaces
 *
 * Diferencias con la versión de agro, de la que se toma la idea:
 *
 *   · El estilo va en el SCSS del theme, no en `style.cssText` desde el JS: allí
 *     no había forma de retocarlo sin editar el código.
 *   · Filtra con una clase (`is-filtered`), no escribiendo `style.display`. En
 *     agro el módulo de permisos usa ese mismo `display` para esconder ítems, y
 *     ambos se pisaban; el filtro tenía que ir mirando estilos en línea ajenos
 *     para no romperlo.
 *   · Ignora tildes: «fertilizacion» encuentra «Fertilización».
 *   · Avisa cuando no hay resultados, y lo anuncia a los lectores de pantalla.
 */

import { normalizeText } from './normalize-text.js';
import { sidenavSearchES } from './locale-es.js';

const CLASS_FILTERED = 'is-filtered';
// Grupo que el buscador abre por su cuenta mientras dura el filtro.
const CLASS_SEARCH_OPEN = 'is-search-open';

function buildSearch() {
  const box = document.createElement('div');
  box.className = 'sidenav-search';
  box.innerHTML = `
    <i class="fas fa-magnifying-glass sidenav-search-icon"></i>
    <input type="search" class="sidenav-search-input" autocomplete="off"
           placeholder="${sidenavSearchES.placeholder}" aria-label="${sidenavSearchES.placeholder}">
  `;
  return box;
}

class SidenavSearch {
  constructor(sidenav) {
    this.sidenav = sidenav;
    this.menu = sidenav.querySelector('.sidenav-menu');
    if (!this.menu) return;

    this.items = Array.prototype.slice.call(this.menu.querySelectorAll('.sidenav-item'));
    this.links = this.menu.querySelectorAll('.sidenav-link');

    // Los de PRIMER nivel, que son los que cuentan como resultado y los que
    // deciden si un rótulo de sección se queda o se va. Los hijos de un grupo
    // viajan con su padre.
    this.top = this.items.filter((item) => item.parentNode === this.menu);

    // Grupos desplegables, cada uno con el elemento de lista que lo contiene.
    this.groups = Array.prototype.slice
      .call(this.menu.querySelectorAll('.sidenav-collapse'))
      .map((list) => ({ list, item: list.closest('.sidenav-item') }))
      .filter((g) => g.item);

    const min = Number(sidenav.getAttribute('data-company-sidenav-search-min')) || 0;
    if (this.links.length <= min) return;

    this._mount();
  }

  _mount() {
    const existing = this.sidenav.querySelector('.sidenav-search input');

    if (existing) {
      this.input = existing;
    } else {
      let header = this.sidenav.querySelector('.sidenav-header');

      if (!header) {
        header = document.createElement('div');
        header.className = 'sidenav-header';
        this.sidenav.insertBefore(header, this.sidenav.firstChild);
      }

      const box = buildSearch();
      header.appendChild(box);
      this.input = box.querySelector('input');
    }

    this.empty = document.createElement('p');
    this.empty.className = 'sidenav-search-empty';
    this.empty.setAttribute('role', 'status');
    this.empty.hidden = true;
    this.empty.textContent = sidenavSearchES.empty;
    this.menu.parentNode.insertBefore(this.empty, this.menu.nextSibling);

    this.input.addEventListener('input', () => this.filter(this.input.value));
    // Escape limpia el filtro sin tener que borrar a mano.
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.input.value = '';
        this.filter('');
      }
    });
  }

  /**
   * Texto PROPIO del ítem: el de su enlace, sin lo que cuelgue de un submenú.
   *
   * Con `textContent` a secas un grupo hereda el texto de todos sus hijos y
   * «Empresas» aparecería al buscar «auditoría». Se vería el título de un grupo
   * que, abierto, no contiene nada llamado así.
   */
  _label(item) {
    const link = item.querySelector(':scope > .sidenav-link');
    return link ? link.textContent : item.textContent;
  }

  /**
   * Un rótulo de sección solo se muestra si alguno de los ítems que lo siguen
   * —hasta el próximo rótulo— ha quedado visible.
   */
  _syncTitles() {
    let title = null;
    let visibles = 0;

    const close = () => {
      if (title) title.classList.toggle(CLASS_FILTERED, visibles === 0);
    };

    this.top.forEach((item) => {
      if (item.classList.contains('sidenav-title')) {
        close();
        title = item;
        visibles = 0;
        return;
      }

      if (!item.classList.contains(CLASS_FILTERED)) visibles += 1;
    });

    close();
  }

  /**
   * Grupos desplegables: lo que se busca puede estar dentro de uno cerrado, y
   * entonces no se vería por mucho que se le quite el filtro. Se fuerzan
   * abiertos los que tengan resultados.
   *
   * Se abre con una clase propia y no con el `show` de MDB a propósito: dejar
   * al componente en un estado que él no puso significa que al limpiar la
   * búsqueda habría que adivinar cuáles estaban abiertos antes. Así el plegado
   * de verdad no se toca y al vaciar el campo el menú vuelve solo a como estaba.
   */
  _syncGroups(term) {
    this.groups.forEach(({ list, item }) => {
      const conResultados =
        !item.classList.contains(CLASS_FILTERED) &&
        !!list.querySelector(`.sidenav-item:not(.${CLASS_FILTERED})`);

      list.classList.toggle(CLASS_SEARCH_OPEN, !!term && conResultados);
    });
  }

  /**
   * @param {string} query
   */
  filter(query) {
    const term = normalizeText(query).trim();

    // Cada ítem contra SU propia etiqueta.
    this.items.forEach((item) => {
      if (item.classList.contains('sidenav-title')) return;

      const hit = !term || normalizeText(this._label(item)).includes(term);
      item.classList.toggle(CLASS_FILTERED, !hit);
    });

    // Y ahora se arregla la familia, en los dos sentidos:
    //
    //   · Si el grupo acierta por su nombre —«Empresas»— se enseña entero. Quien
    //     busca el grupo quiere ver lo que hay dentro, no un grupo vacío.
    //   · Si acierta un hijo, el grupo tiene que verse aunque su nombre no
    //     tenga nada que ver, o el resultado quedaría colgando de la nada.
    this.groups.forEach(({ list, item }) => {
      const hijos = list.querySelectorAll('.sidenav-item');

      if (!item.classList.contains(CLASS_FILTERED)) {
        hijos.forEach((hijo) => hijo.classList.remove(CLASS_FILTERED));
        return;
      }

      const alguno = Array.prototype.some.call(
        hijos,
        (hijo) => !hijo.classList.contains(CLASS_FILTERED)
      );

      if (alguno) item.classList.remove(CLASS_FILTERED);
    });

    this._syncTitles();
    this._syncGroups(term);

    // Se cuentan solo los de primer nivel ya resueltos: un grupo visible cuenta
    // como un resultado aunque acierten tres de sus hijos.
    const matches = this.top.filter(
      (item) =>
        !item.classList.contains('sidenav-title') && !item.classList.contains(CLASS_FILTERED)
    ).length;

    if (this.empty) this.empty.hidden = matches > 0;
    this.sidenav.classList.toggle('is-searching', !!term);
  }

  clear() {
    if (!this.input) return;
    this.input.value = '';
    this.filter('');
  }

  dispose() {
    if (this.empty) this.empty.remove();
    this.items.forEach((item) => item.classList.remove(CLASS_FILTERED));
    this.groups.forEach(({ list }) => list.classList.remove(CLASS_SEARCH_OPEN));
    this.sidenav.classList.remove('is-searching');
    delete this.sidenav.__companySidenavSearch;
  }

  static getInstance(sidenav) {
    return sidenav.__companySidenavSearch || null;
  }
}

/**
 * Activa el buscador en los sidenav que lo pidan.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 */
export function setupSidenavSearch(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-company-sidenav-search]').forEach((sidenav) => {
    if (sidenav.__companySidenavSearch) return;
    sidenav.__companySidenavSearch = new SidenavSearch(sidenav);
  });
}

export { SidenavSearch };
export default setupSidenavSearch;
